/**
 * BilanSyncCard — statut auto-sync bilans → AGO drafts.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Zap, CheckCircle2, AlertTriangle, Clock, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  done: { color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2, label: "Traité" },
  pending: { color: "bg-blue-100 text-blue-700", icon: Clock, label: "En attente" },
  failed: { color: "bg-red-100 text-red-700", icon: AlertTriangle, label: "Échec" },
  skipped: { color: "bg-slate-100 text-slate-500", icon: AlertTriangle, label: "Ignoré" },
};

const formatDateFR = (iso) => {
  if (!iso) return "—";
  try { const [y, m, d] = iso.slice(0, 10).split("-"); return `${d}/${m}/${y}`; } catch { return iso; }
};

export default function BilanSyncCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/legal/bilans/sync-status"); setData(r.data); }
    catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const syncNow = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/legal/bilans/sync-now");
      const msg = `${r.data.done} traité(s), ${r.data.failed} échec, ${r.data.skipped} ignoré(s)`;
      if (r.data.done > 0) toast.success(msg);
      else toast.info(msg);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  if (!data || data.total_bilans === 0) return null;

  return (
    <Card className="border-amber-200" data-testid="bilan-sync-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm flex items-center gap-2">
            <Zap className="w-4 h-4 text-amber-600" />
            Synchro auto bilans → brouillons AGO
            <span className="text-xs text-slate-500 font-normal">
              planification mensuelle + téléversement en direct
            </span>
          </CardTitle>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={syncNow} disabled={syncing} data-testid="bilan-sync-now-btn">
              {syncing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
              Rattraper maintenant
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-4 gap-2 text-xs">
          <div className="p-2 bg-slate-50 rounded text-center">
            <p className="font-bold text-slate-900">{data.total_bilans}</p>
            <p className="text-slate-500 text-[10px] uppercase">Total bilans</p>
          </div>
          <div className="p-2 bg-emerald-50 rounded text-center">
            <p className="font-bold text-emerald-700">{data.done}</p>
            <p className="text-emerald-700 text-[10px] uppercase">Traités</p>
          </div>
          <div className="p-2 bg-blue-50 rounded text-center">
            <p className="font-bold text-blue-700">{data.pending}</p>
            <p className="text-blue-700 text-[10px] uppercase">En attente</p>
          </div>
          <div className="p-2 bg-red-50 rounded text-center">
            <p className="font-bold text-red-700">{data.failed}</p>
            <p className="text-red-700 text-[10px] uppercase">Échec</p>
          </div>
        </div>

        {data.recent?.length > 0 && (
          <div className="border rounded divide-y max-h-60 overflow-y-auto">
            {data.recent.map((r) => {
              const meta = STATUS_META[r.status] || STATUS_META.pending;
              const Icon = meta.icon;
              return (
                <div key={r.id} className="flex items-center justify-between px-3 py-1.5" data-testid={`bilan-sync-row-${r.id}`}>
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-sm truncate">{r.filename}</span>
                    <span className="text-[10px] text-slate-400">{formatDateFR(r.uploaded_at)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {r.exercice && <Badge variant="outline" className="text-[10px]">Ex. {r.exercice}</Badge>}
                    <Badge className={`text-[10px] ${meta.color}`}>{meta.label}</Badge>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
